import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authUrl } from '../utils/authReturn';
import './Header.css';

function Header({ title, subtitle, creditExtra, children }) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // The Home shortcut is redundant on the home page itself, and is only useful
  // to signed-in users (signed-out visitors land on the home directory anyway).
  const showHomeLink = Boolean(user) && location.pathname !== '/';

  // Close on outside click/touch, or on Escape (returning focus to the toggle).
  useEffect(() => {
    if (!menuOpen) return;
    const handlePointer = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        btnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  // When the menu opens, move focus to its first item (menu-button pattern).
  useEffect(() => {
    if (!menuOpen) return;
    menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/');
  };

  // Roving focus among the open menu's items.
  const focusItem = (dir) => {
    const items = menuRef.current
      ? Array.from(menuRef.current.querySelectorAll('[role="menuitem"]'))
      : [];
    if (!items.length) return;
    const i = items.indexOf(document.activeElement);
    const next =
      dir === 'first' ? 0 :
      dir === 'last' ? items.length - 1 :
      dir === 'next' ? (i + 1) % items.length :
      (i - 1 + items.length) % items.length;
    items[next].focus();
  };

  const handleMenuKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); focusItem('next'); break;
      case 'ArrowUp': e.preventDefault(); focusItem('prev'); break;
      case 'Home': e.preventDefault(); focusItem('first'); break;
      case 'End': e.preventDefault(); focusItem('last'); break;
      case 'Tab': setMenuOpen(false); break;
      default: break;
    }
  };

  const handleButtonKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setMenuOpen(true);
    }
  };

  return (
    <header className="site-header">
      <div className="header-content">
        <div className="header-main">
          <div className="header-titles">
            <h1 className="site-title">{title || 'Brookline Notepad'}</h1>
            {subtitle && <p className="site-subtitle">{subtitle}</p>}
            <p className="site-credit">{creditExtra || 'by Michael Rubenstein'}</p>
          </div>

          <div className="header-auth">
            {showHomeLink && (
              <Link to="/" className="header-home-btn" aria-label="Home" title="Home">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M3 9.5 12 3l9 6.5V20a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 13 15 13 15 22"/>
                </svg>
              </Link>
            )}
            {!user ? (
              <button className="header-signin-btn" onClick={() => navigate(authUrl('/signin', location))}>
                Sign In
              </button>
            ) : (
              <div className="header-user-menu-wrapper">
                <button
                  ref={btnRef}
                  className="header-user-btn"
                  onClick={() => setMenuOpen(!menuOpen)}
                  onKeyDown={handleButtonKeyDown}
                  aria-label="User menu"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  title={user.salutation_name || user.full_name || user.email}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </button>
                {menuOpen && (
                  <div className="header-user-menu" ref={menuRef} role="menu" aria-label="User menu" onKeyDown={handleMenuKeyDown}>
                    <div className="header-user-menu-name" role="presentation">{user.salutation_name || user.full_name || user.email}</div>
                    <button role="menuitem" tabIndex={-1} onClick={() => { setMenuOpen(false); navigate('/account'); }}>
                      My Account
                    </button>
                    {user.is_admin && (
                      <button role="menuitem" tabIndex={-1} onClick={() => { setMenuOpen(false); navigate('/admin'); }}>
                        Admin
                      </button>
                    )}
                    <button role="menuitem" tabIndex={-1} onClick={handleLogout}>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {children && <div className="header-extras">{children}</div>}
      </div>
    </header>
  );
}

export default Header;
